// Shared avatar store — color + emoji chosen by the user, persisted
// to localStorage, broadcast to every subscriber so AvatarButton on
// Home updates the moment the user picks something in Settings.
//
// Default avatar is derived from the user id so a brand-new user
// already has a coherent personalized look before they touch anything.

import { useSyncExternalStore } from 'react'

const STORAGE_KEY = 'moonpath-avatar'

// Curated emoji set — friendly characters, weather, plants, food. No faces
// or anything that could feel "assigned identity" to a kid.
export const AVATAR_EMOJIS = [
  '🦊', '🐻', '🐰', '🐯', '🐼', '🦁', '🐸', '🐵',
  '🐱', '🐶', '🐨', '🦄', '🦋', '🐝', '🐢', '🦉',
  '🌸', '🌻', '🍄', '🌟', '🌙', '☀️', '🌈', '⭐',
]

// Hue tokens map directly to the OKLCH ramp defined in src/index.css
// @theme. Face is the -100 stop (soft pastel disk bg), outline is the
// -300 stop (light vivid; used for the disk's border + ledge so the
// avatar reads as a same-hue tonal pair).
export const AVATAR_COLORS = [
  { id: 'lavender', face: 'oklch(0.94 0.05 295)', outline: 'oklch(0.86 0.10 295)' },
  { id: 'blue',     face: 'oklch(0.94 0.05 250)', outline: 'oklch(0.85 0.10 245)' },
  { id: 'cyan',     face: 'oklch(0.94 0.06 220)', outline: 'oklch(0.86 0.10 220)' },
  { id: 'teal',     face: 'oklch(0.94 0.06 180)', outline: 'oklch(0.86 0.10 180)' },
  { id: 'green',    face: 'oklch(0.94 0.07 145)', outline: 'oklch(0.86 0.13 145)' },
  { id: 'yellow',   face: 'oklch(0.95 0.08 95)',  outline: 'oklch(0.91 0.13 95)'  },
  { id: 'orange',   face: 'oklch(0.94 0.05 55)',  outline: 'oklch(0.86 0.11 55)'  },
  { id: 'coral',    face: 'oklch(0.94 0.05 25)',  outline: 'oklch(0.85 0.11 25)'  },
  { id: 'pink',     face: 'oklch(0.94 0.05 5)',   outline: 'oklch(0.86 0.10 5)'   },
]

function hashSeed(seed) {
  let h = 0
  for (let i = 0; i < (seed?.length || 0); i++) h = (h * 31 + seed.charCodeAt(i)) | 0
  return Math.abs(h)
}

export function defaultAvatarFor(seed) {
  const h = hashSeed(seed || 'guest')
  return {
    emoji: AVATAR_EMOJIS[h % AVATAR_EMOJIS.length],
    colorId: AVATAR_COLORS[(h * 7) % AVATAR_COLORS.length].id,
  }
}

let current = readStored() || defaultAvatarFor('guest')
const listeners = new Set()

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed?.emoji || !parsed?.colorId) return null
    if (!AVATAR_COLORS.find(c => c.id === parsed.colorId)) return null
    return parsed
  } catch { return null }
}

function notify() { for (const fn of listeners) fn() }

export function setAvatar(partial) {
  const next = { ...current, ...partial }
  if (next.emoji === current.emoji && next.colorId === current.colorId) return
  current = next
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(current)) } catch {}
  notify()
}

// Seed the store with a derived default if nothing is persisted yet.
// Safe to call multiple times — only writes the first time through.
export function seedAvatar(seed) {
  if (readStored()) return
  current = defaultAvatarFor(seed)
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(current)) } catch {}
  notify()
}

export function getAvatar() { return current }

export function getAvatarColor(colorId) {
  return AVATAR_COLORS.find(c => c.id === colorId) || AVATAR_COLORS[0]
}

export function useAvatar() {
  return useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => listeners.delete(cb) },
    () => current,
  )
}
